import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MonthPickerFieldComponent,
  MonthPickerFieldParams,
} from '@app/form-fields/wrappers/month-picker-field/month-picker-field.component';
import { InputsService } from '@app/inputs/services/inputs.service';
import { TipService } from '@app/onboarding/services/tip.service';
import { UserOutcomeDetail } from '@app/outcomes/outcomes-api.model';
import { AuthService } from '@app/shared/services/auth.service';
import { WindowToken } from '@app/shared/window.token';
import { TagsApi } from '@app/tags/tag-api.model';
import { UserContentModalBaseDirective } from '@app/user-content/user-input/user-content-modal-base/user-content-modal-base.directive';
import {
  DfFormFieldBuilder,
  DfFormFieldConfig,
  DfTemplateOptions,
} from '@lib/fresco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

export interface AccomplishmentFormModel {
  title: string;
  timeSpan: {
    noExpiration?: boolean;
    startDate: Date;
    endDate?: Date;
  };
  tags: TagsApi.Tag[];
}

@Component({
    selector: 'dgx-accomplishment-modal',
    templateUrl: './accomplishment-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccomplishmentModalComponent extends UserContentModalBaseDirective<
  UserOutcomeDetail,
  AccomplishmentFormModel
> {
  public readonly i18n = this.translate.instant([
    // Validation messages
    'AccomplishmentFormCtrl_Title',
    'AccomplishmentFormCtrl_EndDate',
    // used in #titleHelperText
    'dgUserOutcomeEditForm_QuantitativeDescriptions',
    'dgUserOutcomeEditForm_ExpandedClientBase',
    'dgUserOutcomeEditForm_OrganizedCharity',
    // used in modal footer
    'dgUserOutcomeEditForm_SaveToProfile',
  ]);

  @ViewChild('titleHelperText')
  public titleHelperTextRef: TemplateRef<any>;
  @ViewChild('skillsView')
  public skillsViewRef: TemplateRef<any>;

  constructor(
    @Inject(WindowToken) windowRef: Window,
    cdr: ChangeDetectorRef,
    activeModal: NgbActiveModal,
    authService: AuthService,
    tipService: TipService,
    inputsService: InputsService,
    private translate: TranslateService,
    private builder: DfFormFieldBuilder,
    private datePipe: DatePipe
  ) {
    super(
      windowRef,
      cdr,
      activeModal,
      authService,
      tipService,
      inputsService,
      translate.instant('dgUserOutcomeEditForm_AddAccomplishment'),
      translate.instant('dgUserOutcomeEditForm_EditAccomplishment')
    );
  }

  public onTagsChange(items: any[], formControl: FormControl) {
    UserContentModalBaseDirective.setCustomFieldValue(formControl, items);
  }

  public override initControlDependencies() {
    super.initControlDependencies();
    // When start date changes, revalidate the end date, where the range validation error is performed
    // Using elvis operator here only for unit testing only, where the form controls are unfortunately not instantiated yet, likely due to 'shallow' setting
    this.form
      .get('timeSpan.startDate')
      ?.statusChanges.pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        // end date is hidden when form has no expiration
        this.form.get('timeSpan.endDate')?.updateValueAndValidity();
      });
  }

  protected buildFormFields(): DfFormFieldConfig<DfTemplateOptions>[] {
    return [
      this.builder
        .title()
        .labeled('dgUserOutcomeEditForm_DescribeAccomplishment')
        .describedAs(this.titleHelperTextRef)
        .withDgatId('accomplishmentForm-bce')
        .autofocused()
        .validatedByIndexed({
          // prevent empty title
          emptyStringError: {
            expression: (control) => {
              return (control.value || '').trim().length;
            },
            message: this.i18n.AccomplishmentFormCtrl_Title,
          },
        })
        .withErrorMessages({
          // auto-generated 'required' message doesn't make sense with this field name
          required: this.i18n.AccomplishmentFormCtrl_Title,
        })
        .build(),
      this.builder
        .fieldGroup('timeSpan', [
          this.builder
            .checkbox(
              'noExpiration',
              'dgUserOutcomeEditForm_AccomplishmentDate'
            )
            .build(),
          // Start & End Dates
          this.builder
            .fieldGroup(
              '' /* logical group only */,
              [
                this.builder
                  .foreignField<MonthPickerFieldParams>(
                    'startDate',
                    'dgUserOutcomeEditForm_AchievedDate',
                    MonthPickerFieldComponent.REGISTERED_FIELD_TYPE,
                    {
                      isMaxDateToday: true,
                      customDgat: 'accomplishmentForm-startDate',
                    }
                  )
                  .hiddenWhen((_) => !this.model.timeSpan.noExpiration)
                  .asRequired()
                  .styledBy('df-form__col-half')
                  .build(),
                this.builder
                  .foreignField<MonthPickerFieldParams>(
                    'startDate',
                    'dgUserOutcomeEditForm_StartDate',
                    MonthPickerFieldComponent.REGISTERED_FIELD_TYPE,
                    {
                      isMaxDateToday: true,
                      customDgat: 'accomplishmentForm-startDate',
                    }
                  )
                  .hiddenWhen((_) => this.model.timeSpan.noExpiration)
                  .asRequired()
                  // `guts-p-l-0` is needed to strip the left padding for layout, when the 'Achieved on' field is hidden
                  .styledBy('df-form__col-half guts-p-l-0')
                  .build(),
                this.builder
                  .foreignField<MonthPickerFieldParams>(
                    'endDate',
                    'dgUserOutcomeEditForm_EndDate',
                    MonthPickerFieldComponent.REGISTERED_FIELD_TYPE,
                    {
                      isMaxDateToday: true,
                      customDgat: 'accomplishmentForm-endDate',
                    }
                  )
                  .hiddenWhen((_) => this.model.timeSpan.noExpiration)
                  .asRequired()
                  .validatedByIndexed({
                    endDateError: {
                      expression: (control) => {
                        return (
                          control.value > this.model.timeSpan.startDate ||
                          this.model.timeSpan.noExpiration
                        ); // no end date comparison needed for accomplishments without expiration
                      },
                      message: this.i18n.AccomplishmentFormCtrl_EndDate,
                    },
                  })
                  .styledBy('df-form__col-half')
                  .build(),
              ],
              'df-form__row'
            )
            .build(),
        ])
        .asFieldset('dgUserOutcomeEditForm_WhatTimeSpan')
        .build(),
      this.builder
        .customField('tags', 'Core_Skills', this.skillsViewRef, {
          topTags: this.userInterests,
        })
        .unwrapped() // The tag editor has its own label and doesn't require validation, so present it sans fresco's default field wrapper
        .build(),
    ];
  }

  protected initFormModel(source: Partial<UserOutcomeDetail>) {
    this.model = {
      title: source.title,
      timeSpan: {
        noExpiration: !!(source.details?.noExpiration ?? false),
        // Ensure we get Dates, not strings
        startDate: source.startDate ? new Date(source.startDate) : undefined,
        endDate: source.endDate ? new Date(source.endDate) : undefined,
      },
      tags: source.tags ?? [],
    };
    this.cdr.detectChanges();
  }

  protected createResult(
    model: Partial<AccomplishmentFormModel>,
    defaults: Partial<UserOutcomeDetail>
  ): UserOutcomeDetail {
    const result: Partial<UserOutcomeDetail> = {
      ...defaults,
      contentTypeId: 'Accomplishment',
      title: model.title,
      startDate: this.datePipe.transform(
        model.timeSpan.startDate,
        'yyyy-MM-dd'
      ),
      // unset the end date when not needed
      endDate: model.timeSpan?.noExpiration
        ? undefined
        : this.datePipe.transform(model.timeSpan.endDate, 'yyyy-MM-dd'),
      tags: model.tags,
      details: {
        ...defaults.details,
        noExpiration: model.timeSpan.noExpiration,
      },
    };

    return result as UserOutcomeDetail;
  }
}
